import React, { useState, useEffect } from 'react';
import { X, Upload, FileSpreadsheet, Loader2, Save, AlertCircle, CheckCircle, Filter, ArrowDownUp, Edit2, Trash2 } from 'lucide-react';
import { supabase } from '../../../lib/supabaseClient';
import { invokeAdminManageUser } from '../../../lib/adminManageUserClient';
import { isPartnerScopedRole, isGestorParceiroRole } from '../../../lib/authorization';
import { downloadUserTemplate } from '../../../lib/utils';
import readXlsxFile from 'read-excel-file/web-worker';
import ConfirmModal from '../../../components/ConfirmModal.jsx';
import Notification from '../../../components/Notification.jsx';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CreateServiceOrderModal = ({ isOpen, onClose, onSuccess, partner, existingOS = null, readOnly = false }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    cnpj: '',
    requester_user_id: '',
    focal_point_name: '',
    focal_point_email: '',
    focal_point_whatsapp: '',
    additional_focal_points: [],
    desired_start_date: '',
    desired_end_date: '',
    quantity: '',
    has_negotiation: false,
    desired_cycle_id: '',
    course_ids: [],
    observations: '',
    status: 'Criada',
    comments: ''
  });
  const [file, setFile] = useState(null);
  const [additionalFiles, setAdditionalFiles] = useState([]);
  const [negotiationFiles, setNegotiationFiles] = useState([]);
  const [validationResult, setValidationResult] = useState({ total: 0, valid: 0, errors: [], details: [] });
  const [isValidating, setIsValidating] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [availableCourses, setAvailableCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [availableCycles, setAvailableCycles] = useState([]);
  const [loadingCycles, setLoadingCycles] = useState(false);
  const [courseSearch, setCourseSearch] = useState('');
  const [showCourseDropdown, setShowCourseDropdown] = useState(false);
  const courseDropdownRef = React.useRef(null);

  const [existingFiles, setExistingFiles] = useState([]);
  const [existingNegotiationFiles, setExistingNegotiationFiles] = useState([]);
  const [commentsList, setCommentsList] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [commentFilter, setCommentFilter] = useState('all'); // 'all', 'mine', 'requester'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc', 'desc'

  const [existingUserFile, setExistingUserFile] = useState(null);

  const [userProfile, setUserProfile] = useState(null);
  const [creatingCompany, setCreatingCompany] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null });
  const [autoModalOpen, setAutoModalOpen] = useState(false);
  const [autoModalLoading, setAutoModalLoading] = useState(false);
  const [autoModalError, setAutoModalError] = useState('');
  const [autoResult, setAutoResult] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [notification, setNotification] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [requesterOptions, setRequesterOptions] = useState([]);
  const [loadingRequesterOptions, setLoadingRequesterOptions] = useState(false);

  const ensureArray = (value) => (Array.isArray(value) ? value : []);

  const getPhoneDigits = (value) => {
    const digits = String(value || '').replace(/\D/g, '');
    return digits.startsWith('55') && digits.length > 11 ? digits.slice(2) : digits;
  };

  const normalizeDigits = (value) => String(value || '').replace(/\D/g, '');

  const formatCNPJ = (value) => {
    if (!value) return '';
    const digits = value.replace(/\D/g, '');
    const match = digits.match(/^(\d{0,2})(\d{0,3})(\d{0,3})(\d{0,4})(\d{0,2})$/);
    if (!match) return digits;
    return !match[2] ? match[1] 
         : !match[3] ? `${match[1]}.${match[2]}` 
         : !match[4] ? `${match[1]}.${match[2]}.${match[3]}` 
         : !match[5] ? `${match[1]}.${match[2]}.${match[3]}/${match[4]}` 
         : `${match[1]}.${match[2]}.${match[3]}/${match[4]}-${match[5]}`;
  };

  const validateCNPJ = (cnpj) => {
    const digits = String(cnpj).replace(/\D/g, '');
    if (digits.length !== 14) return false;
    if (/^(\d)\1+$/.test(digits)) return false;

    let size = digits.length - 2;
    let numbers = digits.substring(0, size);
    const digitsPart = digits.substring(size);
    let sum = 0;
    let pos = size - 7;

    for (let i = size; i >= 1; i--) {
      sum += numbers.charAt(size - i) * pos--;
      if (pos < 2) pos = 9;
    }
    let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
    if (result !== parseInt(digitsPart.charAt(0))) return false;

    size += 1;
    numbers = digits.substring(0, size);
    sum = 0;
    pos = size - 7;
    for (let i = size; i >= 1; i--) {
      sum += numbers.charAt(size - i) * pos--;
      if (pos < 2) pos = 9;
    }
    result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
    if (result !== parseInt(digitsPart.charAt(1))) return false;

    return true;
  };

  const isValidEmail = (value) => EMAIL_REGEX.test(String(value || '').trim().toLowerCase());

  const isValidPhone = (value) => {
    const digits = getPhoneDigits(value);
    return digits.length === 10 || digits.length === 11;
  };

  useEffect(() => {
    checkUserRole();
    if (isOpen) {
      fetchCourses();
      fetchCycles();
      if (existingOS) {
        setFormData({
          company_name: existingOS.company_name || '',
          cnpj: existingOS.cnpj || '',
          requester_user_id: existingOS.created_by || '',
          focal_point_name: existingOS.focal_point_name || '',
          focal_point_email: existingOS.focal_point_email || '',
          focal_point_whatsapp: existingOS.focal_point_whatsapp || '',
          additional_focal_points: ensureArray(existingOS.additional_focal_points),
          desired_start_date: existingOS.desired_start_date || '',
          desired_end_date: existingOS.desired_end_date || '',
          quantity: existingOS.quantity || '',
          has_negotiation: existingOS.has_negotiation || false,
          desired_cycle_id: existingOS.desired_cycle_id || '',
          course_ids: existingOS.course_ids || [],
          observations: existingOS.observations || '',
          status: existingOS.status || 'Criada',
          comments: '' // We use newComment state for input
        });
        setExistingFiles(existingOS.additional_files || []);
        setExistingNegotiationFiles(existingOS.negotiation_files || []);
        setExistingUserFile(existingOS.user_file_path || null);
        fetchComments(existingOS.id);
      } else {
        // Reset form
        setFormData({
          company_name: '',
          cnpj: '',
          requester_user_id: '',
          focal_point_name: '',
          focal_point_email: '',
          focal_point_whatsapp: '',
          additional_focal_points: [],
          desired_start_date: '',
          desired_end_date: '',
          quantity: '',
          has_negotiation: false,
          desired_cycle_id: '',
          course_ids: [],
          observations: '',
          status: 'Criada',
          comments: ''
        });
        setExistingFiles([]);
        setExistingNegotiationFiles([]);
        setExistingUserFile(null);
        setCommentsList([]);
      }
      setFile(null);
      setAdditionalFiles([]);
      setNegotiationFiles([]);
      setValidationResult({ total: 0, valid: 0, errors: [] });
      setNewComment('');
      setCourseSearch('');
      setShowCourseDropdown(false);
    }
  }, [isOpen, existingOS]);

  // Click outside to close course dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (courseDropdownRef.current && !courseDropdownRef.current.contains(event.target)) {
        setShowCourseDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const checkUserRole = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      setCurrentUserId(user.id);
      const { data: profile } = await supabase.from('profiles').select('role').eq('id', user.id).single();
      if (profile) {
        setUserProfile(profile);
        if (profile.role === 'Admin' || profile.role === 'Super Admin' || profile.role === 'Administrador SAS') {
          setIsAdmin(true);
        }
      }
    }
  };

  const canEditExistingOs = userProfile?.role === 'Admin' || isGestorParceiroRole(userProfile?.role);
  const disableFormFields = readOnly || (existingOS ? (!canEditExistingOs || !editMode) : false);

  const fetchComments = async (osId) => {
    const { data, error } = await supabase
      .from('service_order_comments')
      .select('*, user:profiles(full_name)')
      .eq('service_order_id', osId)
      .order('created_at', { ascending: false }); // Fetch newest first by default
    
    if (!error) {
      setCommentsList(data || []);
    }
  };

  const getFilteredAndSortedComments = () => {
    let filtered = [...commentsList];

    // Filter
    if (commentFilter === 'mine' && currentUserId) {
      filtered = filtered.filter(c => c.user_id === currentUserId);
    } else if (commentFilter === 'requester' && existingOS?.created_by) {
      filtered = filtered.filter(c => c.user_id === existingOS.created_by);
    }

    // Sort
    filtered.sort((a, b) => {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
    });

    return filtered;
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !existingOS) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('service_order_comments')
        .insert({
          service_order_id: existingOS.id,
          user_id: user.id,
          message: newComment.trim()
        });

      if (error) {
        // If error is RLS related to new table not being ready or permission denied for insert
        // Check if user is admin or created_by
        throw error;
      }
      
      setNewComment('');
      fetchComments(existingOS.id);
    } catch (error) {
      console.error('Erro ao adicionar comentário:', error);
      alert('Erro ao adicionar comentário: ' + error.message);
    }
  };

  const handleDeleteAttachment = async (pathToDelete) => {
    if (!confirm('Tem certeza que deseja excluir este anexo?')) return;

    try {
        const newFilesList = existingFiles.filter(p => p !== pathToDelete);
        
        // Update DB
        const { error } = await supabase
            .from('service_orders')
            .update({ additional_files: newFilesList })
            .eq('id', existingOS.id);

        if (error) throw error;

        // Optionally delete from storage (if policy allows)
        // await supabase.storage.from('service-orders').remove([pathToDelete]);

        setExistingFiles(newFilesList);
    } catch (error) {
        console.error('Erro ao excluir anexo:', error);
        alert('Erro ao excluir anexo.');
    }
  };

  const fetchCourses = async () => {
    setLoadingCourses(true);
    try {
      const { data } = await supabase
        .from('courses')
        .select('id, name')
        .eq('status', 'Ativo')
        .order('name');
      setAvailableCourses(data || []);
    } catch (error) {
      console.error('Erro ao buscar cursos:', error);
    } finally {
      setLoadingCourses(false);
    }
  };

  const fetchCycles = async () => {
    setLoadingCycles(true);
    try {
      const { data } = await supabase
        .from('company_cycles')
        .select('id, name, days')
        .order('name');
      setAvailableCycles(data || []);
    } catch (error) {
      console.error('Erro ao buscar ciclos:', error);
    } finally {
      setLoadingCycles(false);
    }
  };

  const toggleCourse = (courseId) => {
    if (readOnly) return;
    
    setFormData(prev => {
      const isSelected = prev.course_ids.includes(courseId);
      if (isSelected) {
        return { ...prev, course_ids: prev.course_ids.filter(id => id !== courseId) };
      } else {
        return { ...prev, course_ids: [...prev.course_ids, courseId] };
      }
    });
    setCourseSearch('');
  };

  const filteredAvailableCourses = availableCourses
    .filter(c => !formData.course_ids.includes(c.id))
    .filter(c => c.name.toLowerCase().includes(courseSearch.toLowerCase()));

  const handleAddFocalPoint = () => {
    setFormData(prev => ({
      ...prev,
      additional_focal_points: [...ensureArray(prev.additional_focal_points), { name: '', email: '', whatsapp: '' }]
    }));
  };

  const handleRemoveFocalPoint = (index) => {
    setFormData(prev => {
      const newPoints = [...ensureArray(prev.additional_focal_points)];
      newPoints.splice(index, 1);
      return { ...prev, additional_focal_points: newPoints };
    });
  };

  const handleAdditionalFocalPointChange = (index, field, value) => {
    setFormData(prev => {
      const newPoints = [...ensureArray(prev.additional_focal_points)];
      newPoints[index] = { ...newPoints[index], [field]: value };
      return { ...prev, additional_focal_points: newPoints };
    });
  };

  const normalizeHeaderText = (value) => String(value || '')
    .replace(/^\uFEFF/, '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

  const resolveHeaderIndexes = (headerRow = []) => {
    const indexes = {
      name: 0,
      email: 1,
      company: 2,
      cpf: 3,
      partner: 4,
      profile: 5,
      whatsapp: 6
    };

    const aliases = {
      name: ['nome completo', 'nome'],
      email: ['e mail', 'email'],
      company: ['empresa', 'empresa opcional', 'empresa (opcional)'],
      cpf: ['cpf', 'cpf 000 000 000 00'],
      partner: ['parceiro sas', 'parceiro', 'parceiro sas opcional', 'parceiro sas (opcional)'],
      profile: ['perfil', 'perfil opcional', 'perfil (opcional)'],
      whatsapp: ['celular', 'celular 00 00000 0000', 'whatsapp', 'whatsapp 00 00000 0000']
    };

    const detected = {
      name: -1,
      email: -1,
      company: -1,
      cpf: -1,
      partner: -1,
      profile: -1,
      whatsapp: -1
    };

    headerRow.forEach((header, index) => {
      const normalized = normalizeHeaderText(header);
      const entry = Object.entries(aliases).find(([, list]) => list.includes(normalized));
      if (entry) {
        detected[entry[0]] = index;
      }
    });

    return {
      name: detected.name !== -1 ? detected.name : indexes.name,
      email: detected.email !== -1 ? detected.email : indexes.email,
      company: detected.company !== -1 ? detected.company : indexes.company,
      cpf: detected.cpf !== -1 ? detected.cpf : indexes.cpf,
      partner: detected.partner !== -1 ? detected.partner : indexes.partner,
      profile: detected.profile !== -1 ? detected.profile : indexes.profile,
      whatsapp: detected.whatsapp !== -1 ? detected.whatsapp : indexes.whatsapp
    };
  };

  const fetchRequesterOptions = async () => {
    if (userProfile?.role !== 'Admin') return;

    setLoadingRequesterOptions(true);
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, email, role, status, sas_partner:sas_partners(name)')
        .order('full_name', { ascending: true });

      if (error) throw error;
      setRequesterOptions(data || []);
    } catch (error) {
      console.error('Erro ao buscar solicitantes:', error);
      setRequesterOptions([]);
    } finally {
      setLoadingRequesterOptions(false);
    }
  };

  useEffect(() => {
    if (isOpen && userProfile?.role === 'Admin') {
      fetchRequesterOptions();
    }
  }, [isOpen, userProfile?.role]);

  const getRequesterLabel = (requester) => {
    if (!requester) return 'Usuário sem identificação';

    const name = requester.full_name || requester.email || 'Usuário sem nome';
    const partner = requester.sas_partner?.name ? ` - Parceiro: ${requester.sas_partner.name}` : '';
    const status = requester.status ? ` - ${requester.status}` : '';
    return `${name} (${requester.role || 'Sem perfil'})${partner}${status}`;
  };

  const resolveCompanyRecord = async () => {
    const cnpjDigits = normalizeDigits(formData.cnpj);
    const companyName = String(formData.company_name || '').trim();

    if (cnpjDigits) {
      const { data, error } = await supabase
        .from('companies')
        .select('id, name, cnpj')
        .eq('cnpj', cnpjDigits)
        .maybeSingle();
      if (error) throw error;
      if (data?.id) return data;
    }

    if (companyName) {
      const { data, error } = await supabase
        .from('companies')
        .select('id, name, cnpj')
        .ilike('name', companyName)
        .limit(10);
      if (error) throw error;
      const exactMatch = (data || []).find((company) => String(company?.name || '').trim().toLowerCase() === companyName.toLowerCase());
      if (exactMatch?.id) return exactMatch;
    }

    return null;
  };

  const getCellValue = (row, index) => {
    if (!Array.isArray(row) || index < 0 || index >= row.length) return '';
    const value = row[index];
    return value === undefined || value === null ? '' : String(value).trim();
  };

  const mapRowsToUsers = (rows) => {
    if (!Array.isArray(rows) || rows.length <= 1) return [];
    const indexes = resolveHeaderIndexes(rows[0] || []);
    const dataRows = rows
      .slice(1)
      .filter(row => Array.isArray(row) && row.some(cell => String(cell ?? '').trim() !== ''));

    return dataRows.map(row => ({
      name: getCellValue(row, indexes.name),
      email: getCellValue(row, indexes.email),
      company: getCellValue(row, indexes.company),
      cpf: getCellValue(row, indexes.cpf),
      partner: getCellValue(row, indexes.partner),
      profile: getCellValue(row, indexes.profile),
      whatsapp: getCellValue(row, indexes.whatsapp)
    }));
  };

  const parseFile = async (fileToParse) => {
    try {
      const fileName = String(fileToParse?.name || '').toLowerCase();
      if (!fileName.endsWith('.xlsx') && !fileName.endsWith('.xls')) {
        throw new Error('Formato inválido. Envie um arquivo Excel (.xlsx ou .xls).');
      }

      const rows = await readXlsxFile(fileToParse);
      return mapRowsToUsers(rows);
    } catch (error) {
      console.error('Error parsing file:', error);
      throw new Error('Erro ao ler arquivo. Verifique o formato.');
    }
  };

  const validateFileContent = async (fileToValidate) => {
    setIsValidating(true);
    setValidationResult({ total: 0, valid: 0, errors: [], details: [] });
    
    try {
      const data = await parseFile(fileToValidate);
      const errors = [];
      const details = [];
      let validCount = 0;

      data.forEach((row, index) => {
        const rowErrors = [];
        const rowIndex = index + 2; // Header is 1

        if (!row.name) rowErrors.push('Nome obrigatório');
        if (!row.email) rowErrors.push('E-mail obrigatório');
        else if (!isValidEmail(row.email)) rowErrors.push('E-mail inválido');
        if (!row.cpf) rowErrors.push('CPF obrigatório');
        if (row.whatsapp && !isValidPhone(row.whatsapp)) rowErrors.push('Celular/WhatsApp inválido');
        
        // Empresa, Parceiro e Perfil são opcionais aqui

        const isValid = rowErrors.length === 0;

        details.push({
          row: rowIndex,
          name: row.name || '-',
          email: row.email || '-',
          whatsapp: row.whatsapp || '-',
          isValid,
          errors: rowErrors
        });

        if (!isValid) {
          errors.push({ row: rowIndex, message: rowErrors.join(', ') });
        } else {
          validCount++;
        }
      });

      setValidationResult({
        total: data.length,
        valid: validCount,
        errors: errors,
        details: details
      });

    } catch (error) {
      setValidationResult({
        total: 0,
        valid: 0,
        errors: [{ row: '-', message: error.message }],
        details: []
      });
    } finally {
      setIsValidating(false);
    }
  };

  const handleFileChange = (e) => {
    if (disableFormFields) return;
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const fileName = String(selectedFile.name || '').toLowerCase();
      if (!fileName.endsWith('.xlsx') && !fileName.endsWith('.xls')) {
        setFile(null);
        setValidationResult({ total: 0, valid: 0, errors: [], details: [] });
        alert('Formato inválido. Use o template em Excel (.xlsx ou .xls).');
        e.target.value = '';
        return;
      }
      setFile(selectedFile);
      validateFileContent(selectedFile);
    }
    // Reset input value to allow selecting the same file again if needed
    e.target.value = '';
  };

  const handleAdditionalFilesChange = (e) => {
    if (e.target.files) {
      setAdditionalFiles(prev => [...prev, ...Array.from(e.target.files)]);
    }
  };

  const removeAdditionalFile = (index) => {
    setAdditionalFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleNegotiationFilesChange = (e) => {
    if (e.target.files) {
      setNegotiationFiles(prev => [...prev, ...Array.from(e.target.files)]);
    }
  };

  const removeNegotiationFile = (index) => {
    setNegotiationFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDeleteNegotiationAttachment = async (pathToDelete) => {
    if (!confirm('Tem certeza que deseja excluir esta evidência?')) return;

    try {
        const newFilesList = existingNegotiationFiles.filter(p => p !== pathToDelete);
        
        // Update DB
        const { error } = await supabase
            .from('service_orders')
            .update({ negotiation_files: newFilesList })
            .eq('id', existingOS.id);

        if (error) throw error;

        setExistingNegotiationFiles(newFilesList);
    } catch (error) {
        console.error('Erro ao excluir evidência:', error);
        alert('Erro ao excluir evidência.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateCNPJ(formData.cnpj)) {
      setNotification({ message: 'CNPJ inválido. Verifique os números digitados.', type: 'error' });
      return;
    }
    
    // Manual validation for fields that HTML 'required' might miss or complex logic
    if (formData.course_ids.length === 0) {
      alert('Por favor, selecione pelo menos um curso.');
      return;
    }

    if (!file && !existingOS && !existingUserFile) {
      alert('O upload do arquivo de usuários é obrigatório.');
      return;
    }
    
    if (validationResult.errors.length > 0) {
      alert('Por favor, corrija os erros no arquivo de usuários antes de continuar.');
      return;
    }

    if (existingOS && userProfile?.role === 'Admin' && !formData.requester_user_id) {
      setNotification({ message: 'Selecione um solicitante para salvar a OS.', type: 'error' });
      return;
    }

    setLoading(true);

    try {
      const companyRecord = await resolveCompanyRecord();
      const resolvedCompanyId = companyRecord?.id || existingOS?.company_id || null;
      if (!resolvedCompanyId) {
        throw new Error('Empresa não encontrada. Crie ou vincule uma empresa válida antes de salvar a OS.');
      }

      let user_file_path = null;
      let additional_files_paths = [];
      let negotiation_files_paths = [];

      // Upload File if exists
      if (file) {
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}_${Math.random().toString(36).substring(2)}.${fileExt}`;
        
        const { data, error: uploadError } = await supabase.storage
          .from('service-orders')
          .upload(fileName, file);

        if (uploadError) {
            throw new Error(`Erro no upload do arquivo: ${uploadError.message}`);
        }
        user_file_path = data?.path || fileName;
      }

      // Upload Additional Files
      if (additionalFiles.length > 0) {
        const uploadPromises = additionalFiles.map(async (f) => {
          const fileExt = f.name.split('.').pop();
          // Use original filename but prepend timestamp to avoid collisions, keeping original name part
          const sanitizedName = f.name.replace(/[^a-zA-Z0-9.-]/g, '_'); 
          const fileName = `attachments/${Date.now()}_${sanitizedName}`;
          
          const { data, error } = await supabase.storage
            .from('service-orders')
            .upload(fileName, f);
            
          if (error) throw new Error(`Erro upload anexo ${f.name}: ${error.message}`);
          return data?.path || fileName;
        });

        additional_files_paths = await Promise.all(uploadPromises);
      }

      // Upload Negotiation Files
      if (formData.has_negotiation && negotiationFiles.length > 0) {
        const uploadPromises = negotiationFiles.map(async (f) => {
          const sanitizedName = f.name.replace(/[^a-zA-Z0-9.-]/g, '_'); 
          const fileName = `negotiations/${Date.now()}_${sanitizedName}`;
          
          const { data, error } = await supabase.storage
            .from('service-orders')
            .upload(fileName, f);
            
          if (error) throw new Error(`Erro upload evidência ${f.name}: ${error.message}`);
          return data?.path || fileName;
        });

        negotiation_files_paths = await Promise.all(uploadPromises);
      }

      const isMissingDesiredCycleColumn = (err) => {
        const message = String(err?.message || '');
        return message.includes('desired_cycle_id') && message.includes('service_orders');
      };

      // Create or Update OS Record
      if (existingOS) {
        // Fetch current files first to append
        let currentFiles = [];
        let currentNegotiationFiles = [];
        if (additional_files_paths.length > 0 || negotiation_files_paths.length > 0) {
           const { data: current } = await supabase.from('service_orders').select('additional_files, negotiation_files').eq('id', existingOS.id).single();
           currentFiles = current?.additional_files || [];
           currentNegotiationFiles = current?.negotiation_files || [];
        }

        const updatePayload = {
          company_id: resolvedCompanyId,
          company_name: formData.company_name,
          cnpj: formData.cnpj,
          created_by: formData.requester_user_id || existingOS.created_by || null,
          focal_point_name: formData.focal_point_name,
          focal_point_email: formData.focal_point_email,
          focal_point_whatsapp: formData.focal_point_whatsapp,
          additional_focal_points: formData.additional_focal_points,
          desired_start_date: formData.desired_start_date || null,
          desired_end_date: formData.desired_end_date || null,
          quantity: formData.quantity ? parseInt(formData.quantity) : 0,
          has_negotiation: formData.has_negotiation,
          desired_cycle_id: formData.desired_cycle_id || null,
          course_ids: formData.course_ids,
          observations: formData.observations,
          status: formData.status,
          comments: formData.comments
        };

        if (user_file_path) {
          updatePayload.user_file_path = user_file_path;
        }

        if (additional_files_paths.length > 0) {
          updatePayload.additional_files = [...currentFiles, ...additional_files_paths];
        }

        if (negotiation_files_paths.length > 0) {
          updatePayload.negotiation_files = [...currentNegotiationFiles, ...negotiation_files_paths];
        }

        let { error: updateError } = await supabase
          .from('service_orders')
          .update(updatePayload)
          .eq('id', existingOS.id);

        if (updateError && isMissingDesiredCycleColumn(updateError)) {
          const { desired_cycle_id, ...fallbackPayload } = updatePayload;
          const retry = await supabase
            .from('service_orders')
            .update(fallbackPayload)
            .eq('id', existingOS.id);
          updateError = retry.error;
        }

        if (updateError) throw updateError;

      } else {
        // Get current user for created_by
        const { data: { user } } = await supabase.auth.getUser();

        const insertPayload = {
          company_id: resolvedCompanyId,
          company_name: formData.company_name,
          cnpj: formData.cnpj,
          focal_point_name: formData.focal_point_name,
          focal_point_email: formData.focal_point_email,
          focal_point_whatsapp: formData.focal_point_whatsapp,
          additional_focal_points: formData.additional_focal_points,
          desired_start_date: formData.desired_start_date || null,
          desired_end_date: formData.desired_end_date || null,
          quantity: formData.quantity ? parseInt(formData.quantity) : 0,
          has_negotiation: formData.has_negotiation,
          desired_cycle_id: formData.desired_cycle_id || null,
          course_ids: formData.course_ids,
          observations: formData.observations,
          user_file_path: user_file_path,
          additional_files: additional_files_paths,
          negotiation_files: negotiation_files_paths,
          status: 'Criada',
          comments: formData.comments,
          created_by: user?.id || null // Add creator ID
        };

        let { error: insertError } = await supabase
          .from('service_orders')
          .insert(insertPayload);

        if (insertError && isMissingDesiredCycleColumn(insertError)) {
          const { desired_cycle_id, ...fallbackPayload } = insertPayload;
          const retry = await supabase
            .from('service_orders')
            .insert(fallbackPayload);
          insertError = retry.error;
        }

        if (insertError) throw insertError;
      }

      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error('Erro ao criar OS:', error);
      alert(`Erro: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    downloadUserTemplate();
  };

  const handleDeleteServiceOrder = async () => {
    if (!existingOS?.id) return;
    try {
      setIsDeleting(true);
      const { error: commentsError } = await supabase
        .from('service_order_comments')
        .delete()
        .eq('service_order_id', existingOS.id);
      if (commentsError) throw commentsError;

      const { error: osError } = await supabase
        .from('service_orders')
        .delete()
        .eq('id', existingOS.id);
      if (osError) throw osError;

      setNotification({ message: 'OS excluída com sucesso!', type: 'success' });
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      const msg = String(err?.message || err || 'Falha ao excluir OS.');
      setNotification({ message: msg, type: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-5xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]">
        <div className="p-6 border-b border-slate-800 flex justify-between items-center">
          <div className="flex flex-col">
            <h3 className="text-xl font-bold text-white">
              {existingOS ? `OS #${existingOS.os_number} - Detalhes` : 'Criar Nova OS'}
            </h3>
            {existingOS && existingOS.creator_info && (
              <p className="text-xs text-slate-400 mt-1">
                {!isPartnerScopedRole(userProfile?.role) && (
                  <>
                    Solicitante: <span className="text-white font-medium">{existingOS.creator_info.name}</span> 
                    {existingOS.creator_info.partner && (
                      <> • Parceiro: <span className="text-orange-400 font-medium">{existingOS.creator_info.partner}</span></>
                    )}
                    <span className="mx-2 text-slate-600">|</span>
                  </>
                )}
                <span className="text-slate-400">Criada em: {new Date(existingOS.created_at).toLocaleDateString()} às {new Date(existingOS.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {canEditExistingOs && existingOS && (
              <button
                type="button"
                onClick={() => setEditMode((v) => !v)}
                className={`p-2 rounded-lg border transition ${editMode ? 'border-orange-500 text-orange-400 bg-orange-500/10' : 'border-slate-700 text-slate-400 hover:text-white hover:border-slate-500'}`}
                title={editMode ? 'Edição ativa' : 'Editar OS'}
              >
                <Edit2 size={18} />
              </button>
            )}
            {userProfile?.role === 'Admin' && existingOS && (
              <button
                type="button"
                disabled={isDeleting}
                onClick={() => {
                  setConfirmModal({
                    isOpen: true,
                    title: `Excluir OS #${existingOS.os_number}`,
                    message: `Esta ação é permanente e remove também os comentários vinculados. Confirmar a exclusão da OS #${existingOS.os_number}?`,
                    onConfirm: async () => {
                      setConfirmModal((prev) => ({ ...prev, isOpen: false }));
                      await handleDeleteServiceOrder();
                    },
                  });
                }}
                className={`p-2 rounded-lg border transition ${isDeleting ? 'border-slate-700 text-slate-500 bg-slate-800/40 cursor-not-allowed' : 'border-rose-500/30 text-rose-400 bg-rose-500/10 hover:bg-rose-500/20 hover:border-rose-400/50'}`}
                title="Excluir OS"
              >
                <Trash2 size={18} />
              </button>
            )}
            <button onClick={onClose} className="text-slate-500 hover:text-white transition">
              <X size={24} />
            </button>
          </div>
        </div>
        
        <div className="p-6 overflow-y-auto flex-1 custom-scrollbar">
          <form id="create-os-form" onSubmit={handleSubmit} className="flex flex-col gap-6">
            {existingOS && userProfile?.role === 'Admin' && (
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                <div className="md:col-span-6 flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-slate-300 uppercase">Solicitante</label>
                  <select
                    value={formData.requester_user_id || ''}
                    onChange={(e) => setFormData({ ...formData, requester_user_id: e.target.value })}
                    className={`bg-slate-950 border border-slate-800 p-3 rounded-lg text-white focus:border-orange-500 outline-none transition ${disableFormFields ? 'cursor-not-allowed text-slate-200 bg-slate-900/50' : ''}`}
                    disabled={disableFormFields || loadingRequesterOptions}
                  >
                    <option value="" disabled>
                      {loadingRequesterOptions ? 'Carregando solicitantes...' : 'Selecione um solicitante...'}
                    </option>
                    {requesterOptions.map((requester) => (
                      <option key={requester.id} value={requester.id}>
                        {getRequesterLabel(requester)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
            
            {/* Top Row: Company, CNPJ, Status */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
              <div className="md:col-span-4 flex flex-col gap-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold text-slate-300 uppercase">Empresa <span className="text-rose-500">*</span></label>
                  {isAdmin && !disableFormFields && (
                    <button
                      type="button"
                      onClick={() => {
                        if (!formData.company_name?.trim() || !formData.cnpj?.trim()) {
                          setNotification({ message: 'Preencha "Empresa" e "CNPJ" para criar a empresa.', type: 'error' });
                          return;
                        }
                        setConfirmModal({
                          isOpen: true,
                          title: 'Criar empresa',
                          message: `Confirmar a criação da empresa "${String(formData.company_name).trim()}" com o CNPJ ${String(formData.cnpj).trim()} no banco?`,
                          onConfirm: async () => {
                            setConfirmModal((prev) => ({ ...prev, isOpen: false }));
                            try {
                              setCreatingCompany(true);
                              const payload = {
                                name: String(formData.company_name || '').trim(),
                                cnpj: normalizeDigits(formData.cnpj),
                                status: 'Ativo',
                              };
                              const { error } = await supabase.from('companies').insert(payload);
                              if (error) throw error;
                              setNotification({ message: 'Empresa criada com sucesso!', type: 'success' });
                            } catch (err) {
                              const msg = String(err?.message || err || 'Falha ao criar empresa.');
                              if (/duplicate|unique/i.test(msg)) {
                                setNotification({ message: 'Já existe uma empresa com este CNPJ.', type: 'error' });
                              } else {
                                setNotification({ message: msg, type: 'error' });
                              }
                            } finally {
                              setCreatingCompany(false);
                            }
                          },
                        });
                      }}
                      disabled={!formData.company_name?.trim() || !formData.cnpj?.trim() || creatingCompany}
                      className={`text-[11px] font-bold flex items-center gap-1 px-2 py-1 rounded border transition ${(!formData.company_name?.trim() || !formData.cnpj?.trim() || creatingCompany) ? 'text-slate-500 border-slate-700 bg-slate-800/40 cursor-not-allowed' : 'text-orange-500 hover:text-orange-400 bg-orange-500/10 border-orange-500/20'}`}
                    >
                      + Criar Empresa
                    </button>
                  )}
                </div>
                <input 
                  type="text" 
                  required
                  value={formData.company_name}
                  onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                  className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white focus:border-orange-500 outline-none transition disabled:bg-slate-900/50 disabled:text-slate-100 disabled:cursor-not-allowed"
                  placeholder="Nome da Empresa"
                  disabled={disableFormFields}
                />
              </div>
              <div className="md:col-span-4 flex flex-col gap-1.5">
                <label className="text-xs font-bold text-slate-300 uppercase">CNPJ <span className="text-rose-500">*</span></label>
                <input 
                  type="text" 
                  required
                  maxLength={18}
                  value={formatCNPJ(formData.cnpj)}
                  onChange={(e) => setFormData({...formData, cnpj: formatCNPJ(e.target.value)})}
                  className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white focus:border-orange-500 outline-none transition disabled:bg-slate-900/50 disabled:text-slate-100 disabled:cursor-not-allowed"
                  placeholder="00.000.000/0000-00"
                  disabled={disableFormFields}
                />
              </div>
              <div className="md:col-span-4 flex flex-col gap-1.5">
                <label className="text-xs font-bold text-slate-300 uppercase">Status <span className="text-rose-500">*</span></label>
                <select 
                  disabled={!existingOS || disableFormFields} // Only editable if existingOS is present (admin logic can be refined)
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className={`bg-slate-950 border border-slate-800 p-3 rounded-lg text-white focus:border-orange-500 outline-none transition ${(!existingOS || disableFormFields) ? 'cursor-not-allowed text-slate-200 bg-slate-900/50' : ''}`}
                >
                  <option value="Criada">Criada</option>
                  <option value="Em análise">Em análise</option>
                  <option value="Em andamento">Em andamento</option>
                  <option value="Aguardando informações">Aguardando informações</option>
                  <option value="Cancelada">Cancelada</option>
                  <option value="Concluída">Concluída</option>
                </select>
              </div>
            </div>

            {/* Second Row: Focal Point Info */}
            <div className="flex flex-col gap-4 bg-slate-950/50 p-4 rounded-xl border border-slate-800/50 relative">
              <div className="flex gap-2 items-center absolute -top-3 right-4 bg-slate-900 px-2">
                {!disableFormFields && (
                  <button 
                    type="button" 
                    onClick={handleAddFocalPoint}
                    className="text-[11px] font-bold text-orange-500 hover:text-orange-400 flex items-center gap-1 bg-orange-500/10 px-2 py-1 rounded border border-orange-500/20 transition"
                  >
                    + Ponto Focal
                  </button>
                )}
              </div>

              {/* Primary Focal Point */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-bold text-slate-300 uppercase">Ponto Focal {ensureArray(formData.additional_focal_points).length > 0 ? 'Principal ' : ''}(Nome) <span className="text-rose-500">*</span></label>
                {isAdmin && !disableFormFields && (
                  <button
                    type="button"
                    onClick={() => {
                      const payload = {
                        name: String(formData.focal_point_name || '').trim(),
                        email: String(formData.focal_point_email || '').trim().toLowerCase(),
                        whatsapp: String(formData.focal_point_whatsapp || '').trim(),
                        companyName: String(formData.company_name || '').trim(),
                        partnerName: existingOS?.creator_info?.partner || partner?.name || null
                      };
                      try { sessionStorage.setItem('prefill_user_from_os', JSON.stringify(payload)); } catch {}
                      try {
                        if (typeof onClose === 'function') onClose();
                        // Solicita mudança de view ao App.jsx
                        window.dispatchEvent(new CustomEvent('sofera:navigate', { detail: { view: 'admin_users' } }));
                        // Passa os dados para o modal de Usuários
                        window.dispatchEvent(new CustomEvent('open-user-create-from-os', { detail: payload }));
                        // Fallback absoluto: se a view não trocar em ~150ms, força URL
                        setTimeout(() => {
                          if (!String(window.location.pathname).includes('/admin/users')) {
                            try { sessionStorage.setItem('prefill_user_from_os', JSON.stringify(payload)); } catch {}
                            window.location.assign('/admin/users');
                          }
                        }, 150);
                      } catch {
                        try { sessionStorage.setItem('prefill_user_from_os', JSON.stringify(payload)); } catch {}
                        window.location.assign('/admin/users');
                      }
                    }}
                    className="ml-2 whitespace-nowrap text-[11px] font-bold text-teal-400 hover:text-teal-300 flex items-center gap-1 bg-teal-500/10 px-2 py-1 rounded border border-teal-500/20 transition"
                    title="Cadastro Automático dos Pontos Focais"
                  >
                    + Cadastro Automático
                  </button>
                )}
                  </div>
                  <input 
                    type="text" 
                    required
                    value={formData.focal_point_name}
                    onChange={(e) => setFormData({...formData, focal_point_name: e.target.value})}
                    className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white focus:border-orange-500 outline-none transition disabled:bg-slate-900/50 disabled:text-slate-100 disabled:cursor-not-allowed"
                    disabled={disableFormFields}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-slate-300 uppercase">E-mail <span className="text-rose-500">*</span></label>
                  <input 
                    type="email" 
                    required
                    value={formData.focal_point_email}
                    onChange={(e) => setFormData({...formData, focal_point_email: e.target.value})}
                    className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white focus:border-orange-500 outline-none transition disabled:bg-slate-900/50 disabled:text-slate-100 disabled:cursor-not-allowed"
                    disabled={disableFormFields}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-slate-300 uppercase">Celular</label>
                  <input 
                    type="text" 
                    value={formData.focal_point_whatsapp}
                    onChange={(e) => setFormData({...formData, focal_point_whatsapp: e.target.value})}
                    className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white focus:border-orange-500 outline-none transition disabled:bg-slate-900/50 disabled:text-slate-100 disabled:cursor-not-allowed"
                    disabled={disableFormFields}
                  />
                </div>
              </div>

              {/* Additional Focal Points */}
              {ensureArray(formData.additional_focal_points).map((fp, index) => (
                <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-slate-800/50 relative animate-fade-in group">
                  {!disableFormFields && (
                    <button 
                      type="button" 
                      onClick={() => handleRemoveFocalPoint(index)}
                      className="absolute top-2 right-0 text-slate-600 hover:text-rose-500 transition opacity-50 group-hover:opacity-100"
                      title="Remover Ponto Focal"
                    >
                      <X size={16} />
                    </button>
                  )}
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-slate-300 uppercase">Ponto Focal {index + 2} (Nome) <span className="text-rose-500">*</span></label>
                    <input 
                      type="text" 
                      required
                      value={fp.name}
                      onChange={(e) => handleAdditionalFocalPointChange(index, 'name', e.target.value)}
                      className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white focus:border-orange-500 outline-none transition disabled:bg-slate-900/50 disabled:text-slate-100 disabled:cursor-not-allowed"
                      disabled={disableFormFields}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-slate-300 uppercase">E-mail <span className="text-rose-500">*</span></label>
                    <input 
                      type="email" 
                      required
                      value={fp.email}
                      onChange={(e) => handleAdditionalFocalPointChange(index, 'email', e.target.value)}
                      className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white focus:border-orange-500 outline-none transition disabled:bg-slate-900/50 disabled:text-slate-100 disabled:cursor-not-allowed"
                      disabled={disableFormFields}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-slate-300 uppercase">Celular</label>
                    <input 
                      type="text" 
                      value={fp.whatsapp}
                      onChange={(e) => handleAdditionalFocalPointChange(index, 'whatsapp', e.target.value)}
                      className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white focus:border-orange-500 outline-none transition disabled:bg-slate-900/50 disabled:text-slate-100 disabled:cursor-not-allowed"
                      disabled={disableFormFields}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Third Row: Cycles & Dates */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-slate-300 uppercase">Ciclo Desejado</label>
                <select
                  value={formData.desired_cycle_id || ''}
                  onChange={(e) => {
                    const cycleId = e.target.value;
                    const selectedCycle = availableCycles.find(c => c.id === cycleId);
                    
                    if (cycleId && selectedCycle && formData.desired_start_date) {
                      const startDate = new Date(formData.desired_start_date);
                      startDate.setDate(startDate.getDate() + selectedCycle.days);
                      const endDate = startDate.toISOString().split('T')[0];
                      setFormData({...formData, desired_cycle_id: cycleId, desired_end_date: endDate});
                    } else {
                      setFormData({...formData, desired_cycle_id: cycleId});
                    }
                  }}
                  className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white focus:border-orange-500 outline-none transition disabled:bg-slate-900/50 disabled:text-slate-100 disabled:cursor-not-allowed"
                  disabled={disableFormFields || loadingCycles}
                >
                  <option value="">Selecione um ciclo...</option>
                  {availableCycles.map(c => (
                    <option key={c.id} value={c.id}>{c.name} ({c.days} dias)</option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-slate-300 uppercase">Início Desejado</label>
                <input 
                  type="date" 
                  value={formData.desired_start_date}
                  onChange={(e) => {
                    const startDateValue = e.target.value;
                    if (startDateValue && formData.desired_cycle_id) {
                      const selectedCycle = availableCycles.find(c => c.id === formData.desired_cycle_id);
                      if (selectedCycle) {
                        const startDate = new Date(startDateValue);
                        startDate.setDate(startDate.getDate() + selectedCycle.days);
                        const endDate = startDate.toISOString().split('T')[0];
                        setFormData({...formData, desired_start_date: startDateValue, desired_end_date: endDate});
                        return;
                      }
                    }
                    setFormData({...formData, desired_start_date: startDateValue});
                  }}
                  className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white focus:border-orange-500 outline-none transition [color-scheme:dark] disabled:bg-slate-900/50 disabled:text-slate-100 disabled:cursor-not-allowed"
                  disabled={disableFormFields}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-slate-300 uppercase">Fim</label>
                <input 
                  type="date" 
                  value={formData.desired_end_date}
                  onChange={(e) => setFormData({...formData, desired_end_date: e.target.value})}
                  className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white focus:border-orange-500 outline-none transition [color-scheme:dark] disabled:bg-slate-900/50 disabled:text-slate-100 disabled:cursor-not-allowed opacity-70"
                  disabled={true}
                />
              </div>
            </div>

            {/* Fourth Row: Quantity & Courses */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
              <div className="md:col-span-3 flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-slate-300 uppercase">Quantidade de Vagas <span className="text-rose-500">*</span></label>
                  <input 
                    type="number" 
                    min="1"
                    required
                    value={formData.quantity}
                    onChange={(e) => setFormData({...formData, quantity: e.target.value})}
                    className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white focus:border-orange-500 outline-none transition disabled:bg-slate-900/50 disabled:text-slate-100 disabled:cursor-not-allowed"
                    disabled={disableFormFields}
                  />
                </div>

                {/* Negociação de Valores Toggle */}
                <div className="mt-4 flex flex-col gap-2">
                  <label className={`flex items-center gap-2 ${disableFormFields ? 'cursor-not-allowed opacity-80' : 'cursor-pointer group'}`}>
                    <div className="relative">
                      <input 
                        type="checkbox" 
                        className="sr-only" 
                        checked={formData.has_negotiation}
                        onChange={(e) => setFormData({...formData, has_negotiation: e.target.checked})}
                        disabled={disableFormFields}
                      />
                      <div className={`block w-10 h-6 rounded-full transition ${formData.has_negotiation ? 'bg-orange-500' : 'bg-slate-800'}`}></div>
                      <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition transform ${formData.has_negotiation ? 'translate-x-4' : ''}`}></div>
                    </div>
                    <span className="text-xs font-bold text-slate-300 group-hover:text-slate-200 transition">Negociação de valores</span>
                  </label>

                  {/* Negociação Evidências Upload */}
                  {formData.has_negotiation && (
                    <div className="mt-2 flex flex-col gap-1.5 animate-fade-in bg-slate-950/50 p-3 rounded-lg border border-orange-500/20">
                      <label className="text-[10px] font-bold text-orange-400 uppercase">Evidências da Negociação</label>
                      
                      <div className="flex flex-col gap-2">
                        <input 
                          type="file" 
                          multiple
                          onChange={handleNegotiationFilesChange} 
                          className="hidden" 
                          id="negotiation-files-upload"
                        />
                        {!disableFormFields && (
                          <label 
                            htmlFor="negotiation-files-upload" 
                            className="cursor-pointer flex items-center justify-center gap-2 bg-slate-900 border border-slate-700 hover:border-orange-500/50 text-slate-300 text-[11px] font-bold px-3 py-2 rounded transition w-full"
                          >
                            <Upload size={14} />
                            Anexar Evidência
                          </label>
                        )}

                        {/* Existing Negotiation Files */}
                        {existingNegotiationFiles.length > 0 && (
                          <div className="flex flex-col gap-1 mt-1">
                            {existingNegotiationFiles.map((path, i) => {
                              const fileName = path.split('/').pop().split('_').slice(1).join('_') || path.split('/').pop();
                              return (
                                <div key={`exist-neg-${i}`} className="flex items-center justify-between bg-slate-900 border border-slate-800 rounded px-2 py-1.5 group">
                                  <span className="text-[10px] text-slate-300 truncate max-w-[120px]">{fileName}</span>
                                  <div className="flex items-center gap-1">
                                    <a 
                                      href={`${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/service-orders/${path}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-teal-500 hover:text-teal-400 p-0.5"
                                      title="Visualizar/Baixar"
                                    >
                                      <Upload size={10} className="rotate-180" />
                                    </a>
                                    {!disableFormFields && (
                                      <button 
                                        type="button"
                                        onClick={() => handleDeleteNegotiationAttachment(path)}
                                        className="text-slate-600 hover:text-rose-500 p-0.5 transition"
                                        title="Excluir"
                                      >
                                        <X size={10} />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* New Negotiation Files */}
                        {negotiationFiles.length > 0 && (
                          <div className="flex flex-col gap-1 mt-1">
                            {negotiationFiles.map((f, i) => (
                              <div key={`new-neg-${i}`} className="flex items-center justify-between bg-slate-900 border border-slate-800 rounded px-2 py-1.5">
                                <span className="text-[10px] text-orange-200 truncate max-w-[140px]">{f.name}</span>
                                <button 
                                  type="button" 
                                  onClick={() => removeNegotiationFile(i)}
                                  className="text-slate-500 hover:text-rose-500 transition p-0.5"
                                >
                                  <X size={10} />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="md:col-span-9 flex flex-col gap-1.5" ref={courseDropdownRef}>
                <div className="flex justify-between items-center mb-0.5">
                  <label className="text-xs font-bold text-slate-300 uppercase">Cursos <span className="text-rose-500">*</span></label>
                  <div className="relative w-1/2">
                    <input
                      type="text"
                      placeholder="Buscar curso..."
                      value={courseSearch}
                      onChange={(e) => {
                        setCourseSearch(e.target.value);
                        setShowCourseDropdown(true);
                      }}
                      onFocus={() => setShowCourseDropdown(true)}
                      className="w-full bg-slate-900 border border-slate-800 px-3 py-1.5 rounded text-xs text-white focus:border-orange-500 outline-none transition disabled:bg-slate-900/50 disabled:text-slate-100 disabled:cursor-not-allowed"
                      disabled={disableFormFields}
                    />
                    {showCourseDropdown && !disableFormFields && (
                      <div className="absolute z-10 w-full mt-1 bg-slate-900 border border-slate-700 rounded-lg shadow-xl max-h-48 overflow-y-auto custom-scrollbar flex flex-col p-1">
                        {filteredAvailableCourses.length > 0 ? (
                          filteredAvailableCourses.map(c => (
                            <button
                              key={c.id}
                              type="button"
                              onClick={() => {
                                toggleCourse(c.id);
                                setShowCourseDropdown(false);
                              }}
                              className="text-left px-3 py-2 rounded hover:bg-slate-800 text-xs text-slate-300 transition"
                            >
                              {c.name}
                            </button>
                          ))
                        ) : (
                          <p className="text-[10px] text-slate-500 italic p-3 text-center">Nenhum curso encontrado.</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Selected Courses List */}
                <div className="bg-slate-950 border border-slate-800 rounded-lg p-3 min-h-[100px] flex flex-wrap gap-2 items-start content-start">
                  {formData.course_ids.map(id => {
                    const course = availableCourses.find(c => c.id === id);
                    if (!course) return null;
                    return (
                      <div key={id} className="flex items-center gap-2 bg-orange-500/10 border border-orange-500/30 text-orange-400 px-3 py-1.5 rounded-full text-xs font-bold animate-fade-in group">
                        <span>{course.name}</span>
                        {!disableFormFields && (
                          <button 
                            type="button" 
                            onClick={() => toggleCourse(id)}
                            className="hover:text-white transition bg-orange-500/20 rounded-full p-0.5"
                            title="Remover curso"
                          >
                            <X size={12} />
                          </button>
                        )}
                      </div>
                    );
                  })}
                  {formData.course_ids.length === 0 && (
                    <div className="w-full h-full flex items-center justify-center">
                      <p className="text-xs text-slate-500 italic">Nenhum curso selecionado. Busque e adicione cursos acima.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Fourth Row: Observations & Upload Side-by-Side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-slate-300 uppercase">Observações</label>
                <textarea 
                  rows="5"
                  value={formData.observations}
                  onChange={(e) => setFormData({...formData, observations: e.target.value})}
                  className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white focus:border-orange-500 outline-none transition resize-none h-full disabled:bg-slate-900/50 disabled:text-slate-100 disabled:cursor-not-allowed"
                  placeholder="Informações adicionais sobre a OS..."
                  disabled={disableFormFields}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <div className="flex justify-between items-center">
                  <label className="text-xs font-bold text-slate-300 uppercase">Upload de Usuários <span className="text-rose-500">*</span></label>
                  <button type="button" onClick={downloadTemplate} className="text-xs text-orange-500 hover:text-orange-400 font-bold underline">
                    Baixar Template
                  </button>
                </div>
                <div className="bg-orange-500/10 border border-orange-500/20 rounded-md p-2 mb-1">
                  <p className="text-[10px] text-orange-300 leading-tight">
                    <span className="font-bold">Atenção:</span> Caso o Ponto Focal também vá realizar o treinamento, os dados dele devem constar na planilha.
                  </p>
                  <p className="text-[10px] text-orange-300/90 leading-tight mt-1">
                    O arquivo valida e-mail no formato nome@dominio.com e celular com 10 ou 11 dígitos, com ou sem +55.
                  </p>
                </div>
                <div className={`border-2 border-dashed rounded-lg p-6 text-center transition h-full flex flex-col justify-center ${file ? (validationResult.errors.length > 0 ? 'border-rose-500/50 bg-rose-500/5' : 'border-teal-500/50 bg-teal-500/5') : (existingUserFile ? 'border-teal-500/50 bg-teal-500/5' : 'border-slate-800 hover:border-slate-700 bg-slate-950')} ${disableFormFields ? 'cursor-not-allowed' : ''}`}>
                  <input 
                    type="file" 
                    accept=".xlsx, .xls" 
                    onChange={handleFileChange} 
                    className="hidden" 
                    id="os-file-upload"
                    disabled={disableFormFields}
                  />
                  <label htmlFor={disableFormFields ? "" : "os-file-upload"} className={`flex flex-col items-center gap-2 ${disableFormFields ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                    {isValidating ? (
                       <>
                        <Loader2 size={32} className="animate-spin text-orange-500" />
                        <p className="text-slate-300 text-xs">Validando arquivo...</p>
                       </>
                    ) : file ? (
                      <>
                        {validationResult.errors.length > 0 ? (
                           <AlertCircle size={32} className="text-rose-500" />
                        ) : (
                           <FileSpreadsheet size={32} className="text-teal-500" />
                        )}
                        <div>
                          <p className={`font-bold text-sm ${validationResult.errors.length > 0 ? 'text-rose-400' : 'text-white'}`}>{file.name}</p>
                          <p className="text-xs text-slate-300">{(file.size / 1024).toFixed(1)} KB</p>
                        </div>
                        
                        {validationResult.total > 0 ? (
                          <div className="flex flex-col items-center gap-1 mt-1">
                            <div className="flex gap-2 text-[10px]">
                              <span className="text-teal-500 font-bold">{validationResult.valid} Válidos</span>
                              {validationResult.errors.length > 0 && (
                                <span className="text-rose-500 font-bold">{validationResult.errors.length} Erros</span>
                              )}
                            </div>
                            <button 
                              type="button" 
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowValidationModal(true); }}
                              className="text-[10px] text-blue-400 hover:text-blue-300 underline font-bold"
                            >
                              Ver Detalhes
                            </button>
                          </div>
                        ) : (
                          <div className="flex gap-2 text-[10px] mt-1">
                            <span className="text-amber-500 font-bold">Arquivo vazio ou sem dados</span>
                          </div>
                        )}

                        <span className="text-xs text-teal-400 font-bold">Clique para trocar</span>
                      </>
                    ) : existingUserFile ? (
                      <>
                        <FileSpreadsheet size={32} className="text-teal-500" />
                        <div>
                          <p className="font-bold text-sm text-white">Arquivo Atual:</p>
                          <a 
                            href={`${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/service-orders/${existingUserFile}`}
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs text-teal-400 underline break-all hover:text-teal-300"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {existingUserFile.split('/').pop()}
                          </a>
                        </div>
                        <span className="text-xs text-slate-300 font-bold mt-2">Clique para substituir</span>
                      </>
                    ) : (
                      <>
                        <Upload size={32} className="text-slate-500" />
                        <div>
                          <p className="text-slate-200 font-medium text-sm">Clique ou arraste o arquivo de usuários</p>
                          <p className="text-[10px] text-slate-400 mt-1">Suporta .xlsx, .xls</p>
                        </div>
                      </>
                    )}
                  </label>
                </div>
              </div>
            </div>
            
            {/* Validation Errors List */}
            {validationResult.errors.length > 0 && (
              <div className="bg-rose-950/20 border border-rose-900/50 rounded-lg p-3 max-h-32 overflow-y-auto custom-scrollbar">
                 <div className="flex items-center gap-2 text-rose-400 mb-2 sticky top-0 bg-rose-950/90 backdrop-blur-sm p-1">
                   <AlertCircle size={14} />
                   <span className="text-xs font-bold">Erros encontrados ({validationResult.errors.length})</span>
                 </div>
                 <div className="space-y-1">
                   {validationResult.errors.map((err, idx) => (
                     <div key={idx} className="text-[10px] text-rose-300 flex gap-2">
                       <span className="font-mono bg-rose-900/40 px-1 rounded">Linha {err.row}</span>
                       <span>{err.message}</span>
                     </div>
                   ))}
                 </div>
              </div>
            )}

            {/* Comments Section */}
            <div className="flex flex-col gap-1.5">
              <div className="flex justify-between items-end">
                <label className="text-xs font-bold text-slate-300 uppercase">Comentários</label>
                {existingOS && (
                  <div className="flex gap-2">
                    <div className="flex bg-slate-950 rounded-lg p-1 border border-slate-800">
                        <button 
                            type="button"
                            onClick={() => setCommentFilter('all')}
                            className={`px-3 py-1 text-[10px] font-bold rounded transition ${commentFilter === 'all' ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-slate-200'}`}
                        >
                            Todos
                        </button>
                        <button 
                            type="button"
                            onClick={() => setCommentFilter('mine')}
                            className={`px-3 py-1 text-[10px] font-bold rounded transition ${commentFilter === 'mine' ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-slate-200'}`}
                        >
                            Meus Comentários
                        </button>
                        <button 
                            type="button"
                            onClick={() => setCommentFilter('requester')}
                            className={`px-3 py-1 text-[10px] font-bold rounded transition ${commentFilter === 'requester' ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-slate-200'}`}
                        >
                            Solicitante
                        </button>
                    </div>
                    <div className="flex bg-slate-950 rounded-lg p-1 border border-slate-800">
                      <button 
                          type="button"
                          onClick={() => setSortOrder('desc')}
                          className={`px-3 py-1 text-[10px] font-bold rounded transition ${sortOrder === 'desc' ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-slate-200'}`}
                          title="Mais recentes primeiro"
                      >
                          Mais Novos
                      </button>
                      <button 
                          type="button"
                          onClick={() => setSortOrder('asc')}
                          className={`px-3 py-1 text-[10px] font-bold rounded transition ${sortOrder === 'asc' ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-slate-200'}`}
                          title="Mais antigos primeiro"
                      >
                          Mais Antigos
                      </button>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Comment History */}
              {existingOS && (
                <div className="bg-slate-950 border border-slate-800 rounded-lg p-3 max-h-48 overflow-y-auto mb-2 custom-scrollbar flex flex-col gap-3">
                  {getFilteredAndSortedComments().length === 0 ? (
                    <p className="text-slate-400 text-sm italic p-2">Nenhum comentário encontrado.</p>
                  ) : (
                    getFilteredAndSortedComments().map((c) => (
                      <div key={c.id} className="bg-slate-900 rounded p-2 border border-slate-800">
                        <div className="flex justify-between items-start mb-1">
                          <span className="text-xs font-bold text-orange-400">
                            {c.user?.full_name || 'Usuário'} {c.user_id === currentUserId && '(Você)'}
                          </span>
                          <span className="text-[10px] text-slate-400">
                            {new Date(c.created_at).toLocaleDateString()} {new Date(c.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                          </span>
                        </div>
                        <p className="text-sm text-slate-100 whitespace-pre-wrap">{c.message}</p>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* New Comment Input */}
              <div className="flex gap-2">
                <textarea 
                  rows="2"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  className="flex-1 bg-slate-950 border border-slate-800 p-3 rounded-lg text-white focus:border-orange-500 outline-none transition resize-none"
                  placeholder="Escreva um comentário..."
                  disabled={!existingOS}
                />
                <button
                  type="button"
                  onClick={handleAddComment}
                  disabled={!existingOS || !newComment.trim()}
                  className="bg-slate-800 hover:bg-orange-600 text-white px-4 rounded-lg font-bold transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Enviar
                </button>
              </div>
            </div>

            {/* Outros Anexos */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold text-slate-300 uppercase">Outros Anexos</label>
              <div className="flex items-start gap-3 flex-wrap">
                <input 
                  type="file" 
                  multiple
                  onChange={handleAdditionalFilesChange} 
                  className="hidden" 
                  id="additional-files-upload"
                />
                <label 
                  htmlFor="additional-files-upload" 
                  className="cursor-pointer flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold px-3 py-2 rounded-lg transition"
                >
                  <Upload size={14} />
                  Adicionar Anexo
                </label>

                {existingFiles.map((path, i) => {
                  const fileName = path.split('/').pop().split('_').slice(1).join('_') || path.split('/').pop();
                  return (
                    <div key={`existing-${i}`} className="flex items-center gap-2 bg-slate-900 border border-slate-700 rounded-md px-2 py-1.5 text-xs text-slate-100 animate-fade-in group">
                      <span className="truncate max-w-[150px]">{fileName}</span>
                      <a 
                        href={`${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/service-orders/${path}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-teal-500 hover:text-teal-400 font-bold ml-1"
                        title="Baixar"
                        download // Try forcing download attribute
                      >
                         <Upload size={12} className="rotate-180" />
                      </a>
                      {isAdmin && (
                        <button 
                          type="button"
                          onClick={() => handleDeleteAttachment(path)}
                          className="text-slate-600 hover:text-rose-500 ml-1 transition"
                          title="Excluir anexo"
                        >
                          <X size={12} />
                        </button>
                      )}
                    </div>
                  );
                })}

                {additionalFiles.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 bg-slate-900 border border-slate-700 rounded-md px-2 py-1.5 text-xs text-slate-100 animate-fade-in">
                    <span className="truncate max-w-[150px]">{f.name}</span>
                    <button 
                      type="button" 
                      onClick={() => removeAdditionalFile(i)}
                      className="text-slate-500 hover:text-rose-500 transition"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

          </form>
        </div>

        <div className="p-6 border-t border-slate-800 flex justify-end gap-3 bg-slate-900">
          <button 
            type="button"
            onClick={onClose} 
            className="px-5 py-2.5 rounded-lg font-bold text-slate-400 hover:bg-white/5 transition"
            disabled={loading}
          >
            Cancelar
          </button>
          
          {existingOS ? (
                <>
                  {!disableFormFields && (
                    <button 
                      type="submit"
                      form="create-os-form"
                      disabled={loading}
                      style={{ backgroundColor: partner?.color || '#f97316' }}
                      className="text-white px-5 py-2.5 rounded-lg font-bold shadow-lg hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {loading ? (
                        <><Loader2 size={18} className="animate-spin" /> Salvando...</>
                      ) : (
                        <><Save size={18} /> Salvar Alterações</>
                      )}
                    </button>
                  )}
                  
                  <button 
                    type="button"
                    disabled
                    className="bg-slate-800 text-slate-500 px-5 py-2.5 rounded-lg font-bold cursor-not-allowed flex items-center gap-2 opacity-50"
                  >
                    <Save size={18} /> Criar OS
                  </button>
                </>
              ) : (
            <button 
              type="submit"
              form="create-os-form"
              disabled={loading}
              style={{ backgroundColor: partner?.color || '#f97316' }}
              className="text-white px-5 py-2.5 rounded-lg font-bold shadow-lg hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <><Loader2 size={18} className="animate-spin" /> Salvando...</>
              ) : (
                <><Save size={18} /> Criar OS</>
              )}
            </button>
          )}
        </div>
      </div>
      {notification && (
        <Notification 
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      {/* Auto-Register Confirmation Modal */}
      {autoModalOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-2xl shadow-2xl overflow-hidden">
            <div className="p-5 border-b border-slate-800 flex justify-between items-center">
              <h3 className="text-lg font-bold text-white">Confirmação de Cadastro Automático</h3>
              <button onClick={() => (!autoModalLoading && setAutoModalOpen(false))} className="text-slate-500 hover:text-white transition"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-3 max-h-[60vh] overflow-y-auto custom-scrollbar">
              <p className="text-sm text-slate-300">Empresa selecionada: <span className="font-bold text-orange-400">{String(formData.company_name || '').trim() || '—'}</span></p>
              <p className="text-sm text-slate-300">Parceiro SAS: <span className="font-bold text-orange-400">{partner?.name || existingOS?.creator_info?.partner || '—'}</span></p>
              <div className="bg-slate-950 border border-slate-800 rounded-lg">
                <div className="px-3 py-2 text-[11px] font-bold text-slate-300 uppercase border-b border-slate-800">Pontos Focais</div>
                <div className="divide-y divide-slate-800">
                  {(() => {
                    const list = [
                      { name: formData.focal_point_name, email: formData.focal_point_email, whatsapp: formData.focal_point_whatsapp }
                    ].concat(formData.additional_focal_points || []);
                    return list.filter(u => (u?.name || '').trim() || (u?.email || '').trim()).map((u, i) => (
                      <div key={i} className="px-3 py-2 flex items-center justify-between">
                        <div>
                          <div className="text-sm text-white font-medium">{u.name || '(Sem nome)'}</div>
                          <div className="text-xs text-slate-400">{u.email || '(sem e-mail)'} • {u.whatsapp || '—'}</div>
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              </div>
              <p className="text-sm text-slate-300">Deseja confirmar o cadastro automático dos Pontos Focais listados na empresa <span className="font-bold text-white">{String(formData.company_name || '').trim() || '—'}</span>?</p>
              {autoModalError && (
                <div className="text-sm text-rose-400 bg-rose-950/30 border border-rose-900/50 rounded p-2">{autoModalError}</div>
              )}
              {autoResult && (
                <div className="text-sm text-slate-200 bg-slate-950 border border-slate-800 rounded p-2">
                  <div className="font-bold mb-1">Resumo</div>
                  <div className="flex gap-4 flex-wrap">
                    <span>Criados no sistema: <span className="text-white font-bold">{autoResult.created_users || 0}</span></span>
                    <span>Convites solicitados ao Supabase: <span className="text-white font-bold">{autoResult.created?.length || 0}</span></span>
                  </div>
                  {autoResult.duplicates?.length > 0 && (
                    <div className="text-amber-300 mt-1">Ignorados por e-mail já existente: {autoResult.duplicates.length}</div>
                  )}
                </div>
              )}
            </div>
            <div className="p-5 border-t border-slate-800 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setAutoModalOpen(false)}
                disabled={autoModalLoading}
                className="px-4 py-2 text-slate-400 font-bold hover:text-white transition disabled:opacity-50"
              >Cancelar</button>
              <button
                type="button"
                onClick={async () => {
                  if (autoModalLoading || autoResult) return;
                  setAutoModalError('');
                  setAutoResult(null);
                  setAutoModalLoading(true);
                  try {
                    const compName = String(formData.company_name || '').trim();
                    if (!compName) {
                      throw new Error('Não é possível criar os usuários Pontos Focais: empresa não encontrada. Por favor, associe os usuários a uma empresa válida.');
                    }
                    const company = await resolveCompanyRecord();
                    if (!company) {
                      throw new Error('Não é possível criar os usuários Pontos Focais: empresa não encontrada. Por favor, associe os usuários a uma empresa válida.');
                    }
                    const candidates = [];
                    const pushIf = (obj) => {
                      const name = String(obj?.name||'').trim();
                      const email = String(obj?.email||'').trim().toLowerCase();
                      const whatsapp = String(obj?.whatsapp||'').trim();
                      if (email && name) candidates.push({ name, email, whatsapp });
                    };
                    pushIf({ name: formData.focal_point_name, email: formData.focal_point_email, whatsapp: formData.focal_point_whatsapp });
                    (formData.additional_focal_points||[]).forEach(fp => pushIf(fp));
                    if (candidates.length === 0) {
                      throw new Error('Nenhum Ponto Focal com nome e e-mail válido para cadastrar.');
                    }
                    const { data: result, error: rpcErr } = await supabase.rpc('admin_auto_register_focal_points', {
                      p_company_id: company.id,
                      p_users: candidates
                    });
                    if (rpcErr) throw rpcErr;
                    // Criar usuários pendentes imediatamente (aparecem na lista)
                    let createdUsers = 0;
                    for (const u of result?.created || []) {
                      try {
                        const placeholderCpf = '9' + String(Date.now()).slice(-10);
                        const { data: createdRes, error: createErr } = await invokeAdminManageUser({
                          action: 'create',
                          payload: {
                            email: u.email,
                            full_name: u.name,
                            cpf: placeholderCpf,
                            role: 'Ponto Focal',
                            status: 'Pendente',
                            company_id: company.id,
                            sas_partner_id: partner?.id || null,
                            whatsapp: u.whatsapp || null
                          },
                        });
                        if (!createErr && !createdRes?.error) createdUsers++;
                      } catch (_) {}
                    }
                    setAutoResult({ ...result, created_users: createdUsers, company });
                  } catch (err) {
                    setAutoModalError(String(err?.message || err || 'Falha ao processar cadastro automático.'));
                  } finally {
                    setAutoModalLoading(false);
                  }
                }}
                className="bg-orange-500 hover:bg-orange-600 px-6 py-2 rounded-lg text-white font-bold shadow-lg transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                disabled={autoModalLoading || !!autoResult}
              >
                {autoModalLoading ? (<><Loader2 size={16} className="animate-spin"/> Processando...</>) : (!!autoResult ? 'Concluído' : 'Confirmar cadastro')}
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
      />

      {/* Validation Details Modal */}
      {showValidationModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[70] p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl">
            <div className="p-4 border-b border-slate-800 flex justify-between items-center bg-slate-950/50 rounded-t-2xl">
              <div>
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <FileSpreadsheet size={20} className="text-blue-500" />
                  Detalhes da Validação
                </h3>
                <p className="text-xs text-slate-400 mt-1">
                  Resumo dos usuários identificados na planilha ({validationResult.valid} válidos, {validationResult.errors.length} inválidos)
                </p>
              </div>
              <button onClick={() => setShowValidationModal(false)} className="text-slate-400 hover:text-white transition bg-slate-800 hover:bg-slate-700 p-2 rounded-lg">
                <X size={20} />
              </button>
            </div>
            
            <div className="p-4 overflow-y-auto flex-1 custom-scrollbar bg-slate-950/20">
              {validationResult.details && validationResult.details.length > 0 ? (
                <div className="overflow-x-auto rounded-lg border border-slate-800">
                  <table className="w-full text-left text-sm whitespace-nowrap">
                    <thead className="bg-slate-900/80 text-slate-400 text-xs uppercase font-bold tracking-wider">
                      <tr>
                        <th className="px-4 py-3 border-b border-slate-800">Linha</th>
                        <th className="px-4 py-3 border-b border-slate-800">Status</th>
                        <th className="px-4 py-3 border-b border-slate-800">Nome</th>
                        <th className="px-4 py-3 border-b border-slate-800">E-mail</th>
                        <th className="px-4 py-3 border-b border-slate-800">Celular</th>
                        <th className="px-4 py-3 border-b border-slate-800">Mensagem</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800/50">
                      {validationResult.details.map((item, idx) => (
                        <tr key={idx} className={`hover:bg-slate-800/30 transition ${item.isValid ? '' : 'bg-rose-950/10'}`}>
                          <td className="px-4 py-3 text-slate-500 font-mono text-xs">{item.row}</td>
                          <td className="px-4 py-3">
                            {item.isValid ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-teal-500/10 text-teal-400 text-[10px] font-bold">
                                <CheckCircle size={12} /> Válido
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-rose-500/10 text-rose-400 text-[10px] font-bold">
                                <AlertCircle size={12} /> Inválido
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-white max-w-[200px] truncate" title={item.name}>{item.name}</td>
                          <td className="px-4 py-3 text-slate-300 max-w-[200px] truncate" title={item.email}>{item.email}</td>
                          <td className="px-4 py-3 text-slate-300 font-mono text-xs">{item.whatsapp}</td>
                          <td className="px-4 py-3">
                            {!item.isValid ? (
                              <span className="text-rose-400 text-xs">{item.errors.join(', ')}</span>
                            ) : (
                              <span className="text-slate-500 text-xs">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-40 text-slate-500">
                  <AlertCircle size={32} className="mb-2 opacity-50" />
                  <p>Nenhum detalhe disponível. Faça o upload de um arquivo para ver a validação.</p>
                </div>
              )}
            </div>
            
            <div className="p-4 border-t border-slate-800 bg-slate-900 rounded-b-2xl flex justify-end">
              <button 
                onClick={() => setShowValidationModal(false)}
                className="px-6 py-2 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-lg transition shadow-lg"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateServiceOrderModal;
